import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { addIcons } from 'ionicons';
import {
  personAddOutline,
  chatbubblesOutline,
  copyOutline,
  arrowRedoOutline,
  bookmark,
  bookmarkOutline,
  book,
  bookOutline,
  documentTextOutline,
  personOutline,
  diceOutline,
} from 'ionicons/icons';
import {
  IonItem,
  IonIcon,
  IonButton,
  ModalController,
  ActionSheetController,
  ActionSheetButton,
  ToastController,
} from '@ionic/angular/standalone';
import { NamedOperation } from 'operationlib';
import { Message } from 'src/app/features/chat/types/message';
import {
  parseSceneGateRolls,
  parseSuggestedActions,
  parseSuggestedMessages,
  parseSheetUpdateRecap,
  parseCorrectionProposals,
  SceneGateRollEntry,
  SuggestedActionEntry,
  LoreCorrectionProposal,
  SuggestedMessageChoice,
} from 'src/app/features/chat/types/extra-data';
import {
  SUGGESTED_ACTION_REGISTRY,
} from 'src/app/features/chat/utils/suggested-action-registry';
import { SelectImpersonateService } from 'src/app/features/chat/services/select-impersonate.service';
import { StoryService } from 'src/app/features/stories/story.service';
import { AuthService } from 'src/app/features/auth/auth.service';
import { SelectOperationComponent } from 'src/app/features/operations/components/select-operation/select-operation.component';
import { createRollModalOptions } from 'src/app/features/operations/utils/roll-modal.util';
import { SupabaseImageComponent } from '../supabase-image/supabase-image.component';
import { MessageContentComponent } from '../message-content/message-content.component';
import {
  SuggestedMessagesModalComponent,
  SuggestedMessageModalResult,
} from '../suggested-messages-modal/suggested-messages-modal.component';
import { RollDefinitionService } from '../../services/roll-definition.service';
import {
  composeSuggestedMessageHtml,
  formatSuggestedMessagePreview,
  formatSuggestedMessageRollDescriptions,
  messageHasRollMarkers,
  stripRollMarkers,
} from '../../utils/suggested-message-compose';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { messageTextPlain } from '../../utils/message-text-plain';
import { LoreCorrectionService } from '../../services/lore-correction.service';

export type SendSuggestedRollPayload = {
  named: NamedOperation;
  characterId: number;
  characterName: string;
};

export type SendSuggestedMessagePayload = {
  characterId: number;
  message: string;
};

@Component({
  imports: [
    CommonModule,
    IonItem,
    IonIcon,
    IonButton,
    SupabaseImageComponent,
    MessageContentComponent,
    ...TRANSLATE_IMPORTS,
  ],
  selector: 're-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
})
export class MessageComponent {
  message = input.required<Message>();
  allowSaveBookmark = input<boolean>(false);
  allowSaveChapter = input<boolean>(false);
  /** Le bolle compatte/orientate sono riservate alle conversazioni dirette. */
  usePrivateMessageLayout = input<boolean>(false);
  /** When false, hide Master suggested rolls (same route flag as chat operations). */
  allowCharacters = input<boolean>(true);
  /** Narratore in elaborazione pre-messaggio: suggerimenti visibili ma non cliccabili. */
  masterLoading = input<boolean>(false);
  /** Solo l'ultimo messaggio del Narratore espone suggerimenti. */
  isLatestMasterMessage = input<boolean>(true);

  private modalController = inject(ModalController);
  private loadingController = inject(LoadingController);
  private actionSheetController = inject(ActionSheetController);
  private toastController = inject(ToastController);
  private rollDefinitionService = inject(RollDefinitionService);
  private impersonateService = inject(SelectImpersonateService, { optional: true });
  private story = inject(StoryService, { optional: true });
  private auth = inject(AuthService, { optional: true });
  private translate = inject(TranslateService);
  private loreCorrection = inject(LoreCorrectionService);

  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private suggestedRepliesModal: HTMLIonModalElement | null = null;

  constructor() {
    addIcons({
      personAddOutline,
      chatbubblesOutline,
      copyOutline,
      arrowRedoOutline,
      bookmark,
      bookmarkOutline,
      book,
      bookOutline,
      documentTextOutline,
      personOutline,
      diceOutline,
    });

    effect(() => {
      if (this.suggestionsLocked() && this.suggestedRepliesModal) {
        void this.suggestedRepliesModal.dismiss();
        this.suggestedRepliesModal = null;
      }
    });
  }

  goToMessage = output<number>();
  quoteMessage = output<number>();
  saveBookmark = output<Message>();
  removeBookmark = output<Message>();
  saveChapter = output<Message>();
  removeChapter = output<Message>();

  goToMessageQuoteHandler(event?: CustomEvent) {
    event?.stopPropagation();
    if (this.message().messageQuoteId)
      this.goToMessage.emit(this.message().messageQuoteId!);
  }

  quoteMessageHandler() {
    if (!this.message().id) return;
    this.quoteMessage.emit(+this.message().id);
  }

  saveBookmarkHandler() {
    this.saveBookmark.emit(this.message());
  }

  removeBookmarkHandler() {
    this.removeBookmark.emit(this.message());
  }

  saveChapterHandler() {
    this.saveChapter.emit(this.message());
  }

  removeChapterHandler() {
    this.removeChapter.emit(this.message());
  }

  onPressStart(event: TouchEvent) {
    this.clearPressTimer();
    this.pressTimer = setTimeout(() => {
      this.pressTimer = null;
      this.openMessageContextMenu(event);
    }, 500);
  }

  onPressEnd() {
    this.clearPressTimer();
  }

  onContextMenu(event: MouseEvent) {
    event.preventDefault();
    this.openMessageContextMenu(event);
  }

  private clearPressTimer() {
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
    }
  }

  private messageHasPersistedId(): boolean {
    return !!this.message().id;
  }

  private async copyMessageText() {
    const plain = messageTextPlain(this.message().text);
    if (!plain) return;
    await navigator.clipboard.writeText(plain);
    const toast = await this.toastController.create({
      message: this.translate.instant('chat.message.actions.copied'),
      duration: 2000,
      position: 'bottom',
    });
    await toast.present();
  }

  async openMessageContextMenu(event: Event) {
    event.stopPropagation();
    if (!this.message().id && this.message().localId) return;

    const buttons: ActionSheetButton[] = [];
    const msg = this.message();

    if (msg.text?.trim()) {
      buttons.push({
        text: this.translate.instant('chat.message.actions.copy'),
        icon: 'copy-outline',
        handler: () => {
          void this.copyMessageText();
        },
      });
    }

    if (this.messageHasPersistedId()) {
      buttons.push({
        text: this.translate.instant('chat.message.actions.quote'),
        icon: 'arrow-redo-outline',
        handler: () => this.quoteMessageHandler(),
      });
    }

    if (this.allowSaveBookmark()) {
      if (this.messagedIsPrivateBookmark()) {
        buttons.push({
          text: this.translate.instant('chat.message.actions.removeBookmark'),
          icon: 'bookmark',
          role: 'destructive',
          handler: () => this.removeBookmarkHandler(),
        });
      } else {
        buttons.push({
          text: this.translate.instant('chat.bookmark.save'),
          icon: 'bookmark-outline',
          handler: () => this.saveBookmarkHandler(),
        });
      }
    }

    if (this.allowSaveChapter()) {
      if (this.messagedIsChapter()) {
        buttons.push({
          text: this.translate.instant('chat.message.actions.removeChapter'),
          icon: 'book',
          role: 'destructive',
          handler: () => this.removeChapterHandler(),
        });
      } else {
        buttons.push({
          text: this.translate.instant('chat.bookmark.saveChapter'),
          icon: 'book-outline',
          handler: () => this.saveChapterHandler(),
        });
      }
    }

    if (msg.impersonate) {
      buttons.push({
        text: this.translate.instant('chat.message.actions.openSheet'),
        icon: 'document-text-outline',
        handler: () => this.showCharacter.emit(msg.impersonate!),
      });
    }

    if (msg.userId) {
      buttons.push({
        text: this.translate.instant('chat.message.actions.openProfile'),
        icon: 'person-outline',
        handler: () => this.showProfile.emit(msg.userId),
      });
    }

    if (!buttons.length) return;

    buttons.push({
      text: this.translate.instant('common.cancel'),
      role: 'cancel',
    });

    const sheet = await this.actionSheetController.create({ buttons });
    await sheet.present();
  }

  showProfile = output<string>();
  showCharacter = output<number>();

  sendSuggestedRoll = output<SendSuggestedRollPayload>();
  suggestedActionClick = output<SuggestedActionEntry>();
  sendSuggestedMessage = output<SendSuggestedMessagePayload>();

  showMasterSuggestions = computed(
    () => this.allowCharacters() && this.isLatestMasterMessage(),
  );
  suggestionsLocked = computed(() => this.masterLoading());
  suggestionsBusyHint = computed(() =>
    this.suggestionsLocked()
      ? this.translate.instant('chat.message.suggestionsBusy')
      : '',
  );

  sceneGateRolls = computed(() =>
    parseSceneGateRolls(this.message().extra_data)
  );

  sheetUpdateRecap = computed(() =>
    parseSheetUpdateRecap(this.message().extra_data)
  );

  suggestedActions = computed(() =>
    parseSuggestedActions(this.message().extra_data)
  );

  visibleSuggestedMessages = computed(() => {
    const owned = new Set(
      (this.impersonateService?.characters() ?? []).map((c) => c.id),
    );
    return parseSuggestedMessages(this.message().extra_data).filter((e) =>
      owned.has(e.characterId),
    );
  });

  showSuggestedReplies = computed(
    () => this.visibleSuggestedMessages().length > 0,
  );

  useProminentSuggestedReplies = computed(
    () => !this.story?.isTableSession(),
  );

  suggestedReplyCharacterName(characterId: number): string {
    return (
      this.story?.characters.data()?.find((c) => c.id === characterId)?.name ??
      ''
    );
  }

  suggestedChoiceLabel(choice: SuggestedMessageChoice): string {
    if (choice.label?.trim()) return choice.label.trim();
    const preview = this.suggestedChoicePreview(choice);
    return preview.length > 72 ? `${preview.slice(0, 72)}…` : preview;
  }

  suggestedChoicePreview(choice: SuggestedMessageChoice): string {
    if (!messageHasRollMarkers(choice.message)) return choice.message;
    const ops = choice.choiceRoll ? [choice.choiceRoll] : [{ name: 'Tiro' }];
    return formatSuggestedMessagePreview(choice.message, ops);
  }

  suggestedChoiceRollDescription(choice: SuggestedMessageChoice): string | null {
    const roll = choice.choiceRoll;
    if (!roll) return null;
    return formatSuggestedMessageRollDescriptions([roll]);
  }

  suggestedChoiceHasRoll(choice: SuggestedMessageChoice): boolean {
    return choice.choiceRoll != null || messageHasRollMarkers(choice.message);
  }

  async openSuggestedRepliesModal(): Promise<void> {
    await this.openSuggestedMessagesModal();
  }

  async pickSuggestedReply(
    characterId: number,
    choiceIndex: number,
    choice: SuggestedMessageChoice,
  ): Promise<void> {
    await this.openSuggestedMessagesModal({ characterId, choiceIndex, choice });
  }

  private async openSuggestedMessagesModal(
    initialSelection?: SuggestedMessageModalResult,
  ): Promise<void> {
    if (this.suggestionsLocked() || !this.isLatestMasterMessage()) return;
    const entries = this.visibleSuggestedMessages();
    if (!entries.length) return;
    const characterIds = entries.map((e) => e.characterId);
    const allChars = this.story?.characters.data() ?? [];
    const characters = allChars.filter(
      (c) => c.id != null && characterIds.includes(c.id!),
    );
    const modal = await this.modalController.create({
      component: SuggestedMessagesModalComponent,
      componentProps: { entries, characters, initialSelection },
      showBackdrop: true,
      initialBreakpoint: 1,
      breakpoints: [0, 1],
    });
    this.suggestedRepliesModal = modal;
    await modal.present();
    const { data } = await modal.onWillDismiss<SuggestedMessageModalResult | null>();
    this.suggestedRepliesModal = null;
    if (!data) return;
    await this.completeSuggestedMessageChoice(data);
  }

  private async completeSuggestedMessageChoice(result: SuggestedMessageModalResult): Promise<void> {
    if (this.suggestionsLocked()) {
      const toast = await this.toastController.create({
        message: this.translate.instant('chat.message.suggestionsBusy'),
        duration: 2500,
        position: 'bottom',
      });
      await toast.present();
      return;
    }
    const char = this.characterForRequest(result.characterId);
    if (!char?.id) return;

    const rollReq = result.choice.choiceRoll;
    if (!rollReq) {
      this.sendSuggestedMessage.emit({
        characterId: result.characterId,
        message: stripRollMarkers(result.choice.message),
      });
      return;
    }

    const storyId = this.story?.id();
    if (!storyId) return;

    const loading = await this.loadingController.create({
      message: this.translate.instant('chat.message.preparingRoll'),
    });
    await loading.present();

    let masterSuggestion: { name: string; definition: string };
    try {
      const generated = await this.rollDefinitionService.fetchForCharacter(
        storyId,
        result.characterId,
        rollReq.name,
        rollReq.description,
      );
      masterSuggestion = {
        name: generated.name,
        definition: generated.definition,
      };
    } catch {
      await loading.dismiss();
      return;
    }

    await loading.dismiss();

    const rollModal = await this.modalController.create(
      createRollModalOptions(SelectOperationComponent, {
        impersonateChar: char,
        storyOperations: [],
        masterSuggestion,
      }),
    );
    await rollModal.present();
    const { data: named } = await rollModal.onDidDismiss<NamedOperation>();
    if (!named) return;

    const messageHtml = composeSuggestedMessageHtml(
      result.choice.message,
      [named],
      result.characterId,
      char.name,
    );
    if (!messageHtml.includes('data-operation')) {
      this.sendSuggestedMessage.emit({
        characterId: result.characterId,
        message: composeSuggestedMessageHtml(
          stripRollMarkers(result.choice.message),
          [named],
          result.characterId,
          char.name,
        ),
      });
      return;
    }
    this.sendSuggestedMessage.emit({
      characterId: result.characterId,
      message: messageHtml,
    });
  }

  actionLabel(entry: SuggestedActionEntry): string {
    const labelKey = SUGGESTED_ACTION_REGISTRY[entry.action]?.labelKey;
    return labelKey
      ? this.translate.instant(labelKey)
      : entry.action;
  }

  actionButtonFill(entry: SuggestedActionEntry): 'outline' | 'solid' {
    return SUGGESTED_ACTION_REGISTRY[entry.action]?.buttonFill ?? 'outline';
  }

  actionIcon(entry: SuggestedActionEntry): string | undefined {
    return SUGGESTED_ACTION_REGISTRY[entry.action]?.icon;
  }

  actionColor(entry: SuggestedActionEntry): string {
    return SUGGESTED_ACTION_REGISTRY[entry.action]?.color ?? 'primary';
  }

  actionEnabled(entry: SuggestedActionEntry): boolean {
    if (!this.allowCharacters()) return false;
    if (this.suggestionsLocked()) return false;
    const allowed = entry.allowedUser;
    if (!allowed || allowed.length === 0) return true;
    const uid = this.auth?.profileSignal()?.id;
    return uid != null && allowed.includes(uid);
  }

  actionDisabledHint(entry: SuggestedActionEntry): string {
    if (this.suggestionsBusyHint()) return this.suggestionsBusyHint();
    if (!this.allowCharacters()) return '';
    const allowed = entry.allowedUser;
    if (!allowed || allowed.length === 0) return '';
    const uid = this.auth?.profileSignal()?.id;
    if (uid == null || !allowed.includes(uid)) {
      return this.translate.instant('chat.message.actionUnavailable');
    }
    return '';
  }

  messagedIsPrivateBookmark = computed(() => {
    return this.message().bookmarks?.some((b) => b.user) ?? false;
  });

  messagedIsChapter = computed(() => {
    return this.message().bookmarks?.some((b) => !b.user) ?? false;
  });

  messageIsBookmark = computed(() => {
    return this.message().meanings?.includes('bookmark');
  });

  messageIsMaster = computed(() => {
    return this.message().meanings?.includes('master') ?? false;
  });

  messageIsLore = computed(() => {
    return this.message().meanings?.includes('lore') ?? false;
  });

  correctionProposals = computed(() =>
    parseCorrectionProposals(this.message().extra_data),
  );

  pendingCorrectionProposals = computed(() =>
    this.correctionProposals().filter((p) => p.status === 'pending'),
  );

  messageIsSystemSeparator = computed(() => {
    return (
      this.message().type === 'system' &&
      !this.messageIsMaster() &&
      !this.messageIsLore()
    );
  });

  publicBookmarks = computed(() => {
    return this.message().bookmarks?.filter((b) => !b.user) ?? [];
  });

  characterForRequest(characterId: number) {
    const list = this.story?.characters.data() ?? [];
    return list.find((c) => c.id === characterId);
  }

  choiceEnabled(characterId: number): boolean {
    if (!this.allowCharacters()) return false;
    const imp = this.impersonateService?.impersonate();
    if (!imp || imp.id !== characterId) return false;
    return this.characterForRequest(characterId) !== undefined;
  }

  choiceDisabledHint(characterId: number): string {
    if (!this.allowCharacters()) return '';
    const target = this.characterForRequest(characterId);
    if (!target)
      return this.translate.instant('chat.message.characterUnavailable');
    const imp = this.impersonateService?.impersonate();
    if (!imp)
      return this.translate.instant('chat.message.impersonateToUse', {
        name: target.name,
      });
    if (imp.id !== characterId)
      return this.translate.instant('chat.message.impersonateToUse', {
        name: target.name,
      });
    return '';
  }

  rollEnabled(op: SceneGateRollEntry): boolean {
    if (this.suggestionsLocked()) return false;
    return this.choiceEnabled(op.characterId);
  }

  rollDisabledHint(op: SceneGateRollEntry): string {
    if (this.suggestionsBusyHint()) return this.suggestionsBusyHint();
    return this.choiceDisabledHint(op.characterId);
  }

  correctionTypeLabel(proposal: LoreCorrectionProposal): string {
    switch (proposal.type) {
      case 'message':
        return this.translate.instant('chat.lore.correctionTypeMessage');
      case 'graph':
        return this.translate.instant('chat.lore.correctionTypeGraph');
      case 'story':
        return this.translate.instant('chat.lore.correctionTypeStory');
      case 'chapter':
        return this.translate.instant('chat.lore.correctionTypeChapter');
      default:
        return '';
    }
  }

  correctionStatusLabel(proposal: LoreCorrectionProposal): string {
    if (proposal.status === 'approved') {
      return this.translate.instant('chat.lore.correctionApproved');
    }
    if (proposal.status === 'rejected') {
      return this.translate.instant('chat.lore.correctionRejected');
    }
    return '';
  }

  async approveCorrection(proposal: LoreCorrectionProposal) {
    const storyId = this.story?.id();
    const messageId = this.message().id;
    if (storyId == null || messageId == null) return;

    const loading = await this.loadingController.create({
      message: this.translate.instant('chat.lore.correctionApplying'),
    });
    await loading.present();

    this.loreCorrection.approve(storyId, proposal.id, messageId).subscribe({
      next: async () => {
        await loading.dismiss();
        const toast = await this.toastController.create({
          message: this.translate.instant('chat.lore.correctionApplied'),
          duration: 2500,
        });
        await toast.present();
      },
      error: async (err: unknown) => {
        console.error('[lore-correction] approve failed', err);
        await loading.dismiss();
        const detail =
          (err && typeof err === 'object' && (err as { error?: { error?: string } }).error?.error) ||
          this.translate.instant('chat.lore.correctionFailed');
        const toast = await this.toastController.create({
          message: detail,
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }

  async rejectCorrection(proposal: LoreCorrectionProposal) {
    const storyId = this.story?.id();
    const messageId = this.message().id;
    if (storyId == null || messageId == null) return;

    this.loreCorrection.reject(storyId, proposal.id, messageId).subscribe({
      error: async (err: unknown) => {
        console.error('[lore-correction] reject failed', err);
        const detail =
          (err && typeof err === 'object' && (err as { error?: { error?: string } }).error?.error) ||
          this.translate.instant('chat.lore.correctionFailed');
        const toast = await this.toastController.create({
          message: detail,
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }

  async openSceneGateRoll(op: SceneGateRollEntry) {
    if (this.suggestionsLocked() || !this.isLatestMasterMessage()) return;
    if (!this.rollEnabled(op)) return;
    const char = this.characterForRequest(op.characterId);
    if (!char) return;
    const modal = await this.modalController.create(
      createRollModalOptions(SelectOperationComponent, {
        impersonateChar: char,
        storyOperations: [],
        masterSuggestion: { name: op.name, definition: op.definition },
      }),
    );
    await modal.present();
    const { data } = await modal.onDidDismiss<NamedOperation>();
    if (data) {
      this.sendSuggestedRoll.emit({
        named: data,
        characterId: op.characterId,
        characterName: char.name,
      });
    }
  }

}
